import React from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Building2, Users, GraduationCap, TrendingDown, Receipt,
  CheckCircle, ArrowRight, MessageCircle, Calendar,
  ChevronRight, Percent, Shield, BookOpen, Award
} from "lucide-react";
import { WA_LINK, CALENDLY } from "@/Layout";

const VOLUME_TIERS = [
  { min: 10, label: "10", discount: "Promo Lancio", price: 199, half: "99,50", badge: null, color: "border-gray-200" },
  { min: 20, label: "20", discount: "-10%", price: 179, half: "89,50", badge: "-10%", color: "border-blue-300" },
  { min: 30, label: "30", discount: "-20%", price: 159, half: "79,50", badge: "-20%", color: "border-amber-300" },
  { min: 50, label: "50+", discount: "-30%", price: 139, half: "69,50", badge: "-30%", color: "border-green-400" },
];

const MODULES = [
  "Verificare e reclamare la scheda Google",
  "Correggere nome, indirizzo, orari e categoria",
  "Aggiungere foto professionali che attirano clienti",
  "Gestire e rispondere alle recensioni",
  "Leggere e interpretare le statistiche",
  "Evitare le violazioni che causano la sospensione",
  "Ottimizzare la descrizione per la ricerca locale",
  "Pubblicare post, offerte e aggiornamenti",
];

export default function EntiPage() {
  const openWA = (msg) => window.open(`${WA_LINK}?text=${encodeURIComponent(msg)}`, '_blank');

  return (
    <div className="min-h-screen bg-[#faf8f5]">

      {/* Hero */}
      <section className="bg-gray-900 text-white py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-900 to-[#5C1A1B]/30"></div>
        <div className="max-w-4xl mx-auto px-4 relative z-10">
          <Badge className="bg-white/10 text-white border-white/20 mb-4 rounded-full px-4 py-1.5 text-xs" data-testid="enti-badge">
            <Building2 className="w-4 h-4 mr-2" />
            Per Enti e Associazioni di Categoria
          </Badge>
          <h1 className="text-3xl md:text-5xl font-black font-google mb-4 leading-tight">
            Formazione digitale{" "}
            <span className="text-amber-400">deducibile al 100%</span>{" "}
            per i Vostri iscritti
          </h1>
          <p className="text-gray-300 text-base md:text-lg max-w-2xl mb-8 leading-relaxed">
            Il 46% dei consumatori cerca attivita' locali su Google ogni giorno.
            La maggior parte delle PMI italiane non ha una scheda ottimizzata.
            Non e' negligenza — e' mancanza di formazione.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button className="bg-amber-400 hover:bg-amber-300 text-gray-900 font-bold rounded-full px-6"
              onClick={() => openWA("Buongiorno, scrivo per conto di [nome ente]. Vorrei informazioni sulla convenzione schedabusiness.com Academy per i nostri iscritti.")}
              data-testid="enti-cta-whatsapp">
              <MessageCircle className="w-4 h-4 mr-2" /> Richiedi Informazioni
            </Button>
            <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-6"
              onClick={() => window.open(CALENDLY, '_blank')}
              data-testid="enti-cta-calendly">
              <Calendar className="w-4 h-4 mr-2" /> Videochiamata Conoscitiva
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-12">

        {/* Il Problema */}
        <section className="mb-14" data-testid="enti-problema">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-google mb-6">
            Il <span className="text-[#ff6b35]">problema</span> che nessuno racconta
          </h2>
          <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-sm">
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { num: "46%", desc: "dei consumatori cerca attivita' locali su Google ogni giorno", src: "Google, 2025" },
                { num: "80%", desc: "delle schede Google delle PMI italiane e' incompleto o non verificato", src: "Stima di settore" },
                { num: "56%", desc: "delle attivita' con scheda sospesa non sa come ripristinarla", src: "Ricerca schedabusiness" },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="text-3xl md:text-4xl font-black text-[#ff6b35] font-google mb-1">{s.num}</div>
                  <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                  <p className="text-[10px] text-gray-400 mt-1">{s.src}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900">
              <strong>Tradotto:</strong> i Vostri iscritti perdono clienti ogni giorno perche' la loro scheda Google non e' visibile, e' incompleta o e' stata sospesa. Non e' colpa loro — semplicemente, nessuno li ha mai informati.
            </div>
          </div>
        </section>

        {/* La Soluzione */}
        <section className="mb-14" data-testid="enti-soluzione">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-google mb-6">
            La <span className="text-[#ff6b35]">soluzione</span>: schedabusiness.com Academy
          </h2>
          <div className="bg-white rounded-2xl border border-gray-200 p-6 md:p-8 shadow-sm">
            <p className="text-gray-600 text-sm mb-6 leading-relaxed">
              Un programma di videoformazione pratica che insegna ai Vostri iscritti a gestire in autonomia la propria scheda Google Business Profile. 8 moduli, dal livello base all'ottimizzazione avanzata.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {MODULES.map((m, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-700 py-2">
                  <div className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-xs font-bold">{i + 1}</div>
                  {m}
                </div>
              ))}
            </div>
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: BookOpen, label: "Videoformazione on-demand" },
                { icon: Users, label: "Live streaming interattive" },
                { icon: Award, label: "Attestato schedabusiness.com" },
                { icon: MessageCircle, label: "Supporto AI gratuito" },
              ].map((f, i) => (
                <div key={i} className="bg-gray-50 rounded-xl p-3 text-center">
                  <f.icon className="w-5 h-5 text-[#ff6b35] mx-auto mb-1" />
                  <p className="text-[10px] text-gray-600 font-medium">{f.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Le 3 Opzioni */}
        <section className="mb-14" data-testid="enti-opzioni">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-google mb-2">
            Tre <span className="text-[#ff6b35]">opzioni</span>, una scelta intelligente
          </h2>
          <p className="text-gray-500 text-sm mb-6">Prezzo di listino: €249/azienda. Convenzione enti: <strong>€199/azienda</strong> (min. 10 iscritti).</p>

          <div className="grid md:grid-cols-3 gap-4">
            {/* Opzione A */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Opzione A</div>
              <h3 className="font-bold text-gray-900 mb-1 text-sm">L'Ente investe per gli iscritti</h3>
              <p className="text-xs text-gray-500 mb-4">L'Ente sostiene interamente il costo della formazione.</p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">L'Ente paga</span>
                  <span className="font-bold text-gray-900">€199/iscritto</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">L'iscritto paga</span>
                  <span className="font-bold text-green-600">€0</span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span className="text-gray-500">Totale 10 iscritti</span>
                  <span className="font-bold">€1.990</span>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-xs text-green-600">
                <Receipt className="w-3.5 h-3.5" />
                <span>Deducibile al 100% per l'Ente</span>
              </div>
            </div>

            {/* Opzione B */}
            <div className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Opzione B</div>
              <h3 className="font-bold text-gray-900 mb-1 text-sm">L'iscritto investe su se stesso</h3>
              <p className="text-xs text-gray-500 mb-4">L'Ente promuove, l'iscritto aderisce autonomamente.</p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">L'Ente paga</span>
                  <span className="font-bold text-green-600">€0</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">L'iscritto paga</span>
                  <span className="font-bold text-gray-900">€199</span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span className="text-gray-500">Totale 10 iscritti</span>
                  <span className="font-bold">€1.990</span>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-xs text-green-600">
                <Receipt className="w-3.5 h-3.5" />
                <span>Deducibile al 100% per l'iscritto</span>
              </div>
            </div>

            {/* Opzione C - Highlighted */}
            <div className="bg-white rounded-2xl border-2 border-[#ff6b35] p-5 shadow-lg relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <div className="bg-[#ff6b35] text-white text-[10px] font-black px-3 py-1 rounded-full">LA PIU' SCELTA</div>
              </div>
              <div className="text-xs font-bold text-[#ff6b35] uppercase tracking-wider mb-2">Opzione C</div>
              <h3 className="font-bold text-gray-900 mb-1 text-sm">50 e 50 — Costi condivisi</h3>
              <p className="text-xs text-gray-500 mb-4">Meta' l'Ente, meta' l'iscritto. Entrambi deducono.</p>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">L'Ente paga</span>
                  <span className="font-bold text-gray-900">€99,50/iscritto</span>
                </div>
                <div className="flex justify-between py-1.5 border-b border-gray-100">
                  <span className="text-gray-500">L'iscritto paga</span>
                  <span className="font-bold text-gray-900">€99,50</span>
                </div>
                <div className="flex justify-between py-1.5">
                  <span className="text-gray-500">Totale 10 iscritti</span>
                  <span className="font-bold">€995 + €995</span>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-xs text-green-600">
                <Receipt className="w-3.5 h-3.5" />
                <span className="font-bold">Deducibile al 100% per ENTRAMBI</span>
              </div>
            </div>
          </div>
        </section>

        {/* Volume Discounts */}
        <section className="mb-14" data-testid="enti-volume">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-google mb-2">
            Piu' iscritti, <span className="text-[#ff6b35]">meno si spende</span>
          </h2>
          <p className="text-gray-500 text-sm mb-6">Sconti progressivi per le convenzioni con piu' iscritti.</p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {VOLUME_TIERS.map((t, i) => (
              <div key={i} className={`bg-white rounded-2xl border-2 ${t.color} p-4 shadow-sm text-center relative ${i === 3 ? 'ring-2 ring-green-400/50' : ''}`}>
                {t.badge && (
                  <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                    <div className={`text-white text-[10px] font-black px-2.5 py-0.5 rounded-full ${i === 3 ? 'bg-green-500' : i === 2 ? 'bg-amber-500' : 'bg-blue-500'}`}>{t.badge}</div>
                  </div>
                )}
                <div className="text-xs text-gray-400 font-bold uppercase mb-1">{t.label} iscritti</div>
                <div className="text-2xl font-black text-gray-900 font-google">€{t.price}</div>
                <div className="text-[10px] text-gray-400 mb-2">/persona</div>
                <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-2">
                  <span className="font-bold">Opzione C:</span><br />
                  €{t.half} + €{t.half}
                </div>
              </div>
            ))}
          </div>

          {/* Esempio concreto */}
          <div className="mt-6 bg-green-50 border border-green-200 rounded-2xl p-5">
            <h3 className="font-bold text-green-900 text-sm mb-2 flex items-center gap-2">
              <TrendingDown className="w-4 h-4" /> Esempio concreto — 50 iscritti, Opzione C
            </h3>
            <div className="grid md:grid-cols-3 gap-4 text-xs">
              <div>
                <span className="text-green-700">L'Ente investe:</span>
                <div className="text-lg font-black text-green-900">€3.475</div>
                <span className="text-green-600">€69,50 x 50 — deducibile 100%</span>
              </div>
              <div>
                <span className="text-green-700">Ogni iscritto investe:</span>
                <div className="text-lg font-black text-green-900">€69,50</div>
                <span className="text-green-600">deducibile 100%</span>
              </div>
              <div>
                <span className="text-green-700">Risultato:</span>
                <div className="text-lg font-black text-green-900">50 aziende formate</div>
                <span className="text-green-600">Costo reale dopo deduzione: potenzialmente €0</span>
              </div>
            </div>
          </div>
        </section>

        {/* Perche' l'Ente */}
        <section className="mb-14" data-testid="enti-perche">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 font-google mb-6">
            Perche' il <span className="text-[#ff6b35]">Vostro</span> ente dovrebbe informarsi
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: "Formazione digitale deducibile", desc: "Rientra tra le spese per l'aggiornamento professionale. Deducibile al 100% sia per l'ente che per il singolo iscritto." },
              { title: "Servizio concreto per gli associati", desc: "Non teoria, ma competenze operative immediatamente applicabili. Ogni iscritto impara a valorizzare la propria scheda Google in autonomia." },
              { title: "Rafforza il ruolo dell'ente", desc: "Proporre formazione digitale posiziona l'associazione come punto di riferimento per l'innovazione delle PMI del territorio." },
              { title: "Supporto permanente incluso", desc: "Ogni iscritto riceve accesso gratuito e permanente all'assistente AI integrato nel sito, per dubbi e aggiornamenti futuri." },
            ].map((b, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm mb-1">{b.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Chi e' Riccardo */}
        <section className="mb-14" data-testid="enti-chi">
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <div className="w-24 h-24 bg-gray-100 rounded-full overflow-hidden flex items-end justify-center relative flex-shrink-0">
                <img src="https://customer-assets.emergentagent.com/job_riccardo-sos-google/artifacts/4eu97hbx_Test%20ico%20TNX%20Ricca.png"
                  alt="Riccardo Rossi" className="w-full h-auto object-cover absolute bottom-0"
                  style={{ clipPath: 'inset(35% 0 0 0)', transform: 'translateY(-15%)' }} />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-lg font-google mb-1">Riccardo Rossi</h3>
                <p className="text-xs text-gray-500 leading-relaxed">
                  Fotografo Google Street View Certificato · Google Local Guide dal 2015 ·
                  Selezionato da Google per il programma Ricoh Theta Z ·
                  7.000+ foto pubblicate · 50.000.000+ visualizzazioni su Google Maps ·
                  Fondatore di schedabusiness.com · Sede: Le Grazie, Siena, Toscana
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Finale */}
        <section className="mb-8" data-testid="enti-cta-finale">
          <div className="bg-gray-900 text-white rounded-3xl p-8 text-center">
            <h2 className="text-xl md:text-2xl font-bold font-google mb-2">Volete saperne di piu'?</h2>
            <p className="text-gray-400 text-sm mb-6 max-w-lg mx-auto">
              Nessun impegno, nessuna pressione. Siamo qui per informare, non per vendere.
              Una videochiamata di 15 minuti e' sufficiente per capire se questa opportunita'
              e' adatta ai Vostri associati.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button className="bg-amber-400 hover:bg-amber-300 text-gray-900 font-bold rounded-full px-6"
                onClick={() => openWA("Buongiorno, scrivo per conto di [nome ente]. Vorrei approfondire la convenzione schedabusiness.com Academy per i nostri iscritti.")}
                data-testid="enti-bottom-whatsapp">
                <MessageCircle className="w-4 h-4 mr-2" /> Scriveteci su WhatsApp
              </Button>
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 rounded-full px-6"
                onClick={() => window.open(CALENDLY, '_blank')}
                data-testid="enti-bottom-calendly">
                <Calendar className="w-4 h-4 mr-2" /> Prenotate una Videochiamata
              </Button>
            </div>
            <p className="text-gray-500 text-[10px] mt-6">
              Questa pagina ha carattere puramente informativo. I prezzi si intendono + IVA dove applicabile.
              La deducibilita' e' soggetta alla normativa vigente. Si consiglia di consultare il proprio commercialista.
            </p>
          </div>
        </section>

      </div>
    </div>
  );
}
